import React, { useState } from 'react';
import { X, Clock, UserCircle2, ChevronRight } from 'lucide-react';
import { useUpcomingLeaves } from '../../../../hooks/manager_portal_hooks';
import TeamMemberDetailModal from '../../TeamMemberDetailModal';

/**
 * Staff On Leave Modal
 *
 * Opened from the "Staff on leave (in next 30 days)" card on the Manager
 * Dashboard. Lists the manager's team members' approved leaves starting within
 * the next 30 days (sourced from `useUpcomingLeaves` — the same query that feeds
 * the card count, source /data/LeaveRequestsV2, so no additional API call is
 * made here).
 *
 * Each row shows the member, leave type, period and day count; selecting a
 * member opens the shared TeamMemberDetailModal, keeping the drill-down
 * consistent with the other dashboard modals and the "My Team" tab.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatPeriod = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (!start) return null;
  if (!end || end === start) return start;
  return `${start} – ${end}`;
};

const UpcomingLeavesModal = ({ onClose }) => {
  const { upcomingLeaves, isLoading, error } = useUpcomingLeaves();
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Staff on leave in the next 30 days"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Staff on Leave (in next 30 days)</h3>
                <p className="text-sm text-gray-500">
                  {isLoading
                    ? 'Loading…'
                    : `${upcomingLeaves.length} leave${upcomingLeaves.length === 1 ? '' : 's'} in the next 30 days`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading staff on leave…</p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-red-500 text-sm">Failed to load staff on leave. Please try again.</p>
              </div>
            ) : upcomingLeaves.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500 text-sm">No staff on leave in the next 30 days.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {upcomingLeaves.map((leave) => {
                  const period = formatPeriod(leave.startDate, leave.endDate);
                  return (
                    <li key={leave.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedMember(leave)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/40 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <UserCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{leave.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {leave.leaveTypeName || 'Leave'}
                            {period ? ` · ${period}` : ''}
                          </p>
                        </div>
                        {leave.days ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 bg-blue-50 text-blue-700 ring-blue-200">
                            {leave.days} day{leave.days === 1 ? '' : 's'}
                          </span>
                        ) : null}
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selectedMember && (
        <TeamMemberDetailModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </>
  );
};

export default UpcomingLeavesModal;
